import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { toast } from '../toast';
import type {
  CoachIndividualBookingResponse,
  CreateIndividualBookingRequest,
  CreateIndividualSlotRequest,
  IndividualClassSettingsResponse,
  IndividualCoachResponse,
  IndividualPricePreviewResponse,
  IndividualScheduleResponse,
  IndividualSlotResponse,
  IndividualBookingType,
  MyIndividualBookingResponse,
  OnSiteBillResponse,
  OnSitePaymentMethod,
  UpdateIndividualClassSettingsRequest,
  UpdateIndividualSlotRequest,
} from '@sportschool/shared-types';

export interface IndividualSlotFilters {
  from?: string;
  to?: string;
  coachMemberId?: string;
  locationId?: string;
  duration?: number;
}

const base = (schoolId: string) => `/schools/${schoolId}/individual-classes`;

/** Surfaces backend business-rule errors (translated from `errors.<code>` by the toast renderer). */
function toastApiError(error: any, fallback: string) {
  const data = error?.response?.data;
  const detail = data?.detail || data?.Detail;
  const code = data?.code || data?.Code;
  const args = data?.args || data?.Args;
  toast.error(detail || fallback, code, args);
}

function useInvalidateIndividual(schoolId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['individualClasses', schoolId] });
    qc.invalidateQueries({ queryKey: ['bills'] });
  };
}

// ── Settings ──────────────────────────────────────────────────────────────

export function useIndividualClassSettings(schoolId: string) {
  return useQuery<IndividualClassSettingsResponse>({
    queryKey: ['individualClasses', schoolId, 'settings'],
    queryFn: async () => (await api.get(`${base(schoolId)}/settings`)).data,
    enabled: !!schoolId,
  });
}

export function useUpdateIndividualClassSettings(schoolId: string) {
  const invalidate = useInvalidateIndividual(schoolId);
  return useMutation({
    mutationFn: async (data: UpdateIndividualClassSettingsRequest) =>
      (await api.put(`${base(schoolId)}/settings`, data)).data as IndividualClassSettingsResponse,
    onSuccess: () => {
      invalidate();
      toast.success('Settings saved', 'individualClasses.toasts.settingsSaved');
    },
    onError: (e) => toastApiError(e, 'Failed to save settings'),
  });
}

export function useIndividualClassCoaches(schoolId: string, onlyEnabled = true) {
  return useQuery<IndividualCoachResponse[]>({
    queryKey: ['individualClasses', schoolId, 'coaches', onlyEnabled],
    queryFn: async () => (await api.get(`${base(schoolId)}/coaches`, { params: { onlyEnabled } })).data,
    enabled: !!schoolId,
  });
}

export function useSetCoachIndividualEligibility(schoolId: string) {
  const invalidate = useInvalidateIndividual(schoolId);
  return useMutation({
    mutationFn: async ({ coachMemberId, canOfferIndividualClasses }: { coachMemberId: string; canOfferIndividualClasses: boolean }) =>
      (await api.put(`${base(schoolId)}/coaches/${coachMemberId}`, { canOfferIndividualClasses })).data as IndividualCoachResponse,
    onSuccess: () => {
      invalidate();
      toast.success('Saved', 'individualClasses.toasts.coachEligibilitySaved');
    },
    onError: (e) => toastApiError(e, 'Failed to save'),
  });
}

// ── Student ───────────────────────────────────────────────────────────────

export function useFreeIndividualSlots(schoolId: string, filters: IndividualSlotFilters, enabled = true) {
  return useQuery<IndividualSlotResponse[]>({
    queryKey: ['individualClasses', schoolId, 'slots', filters],
    queryFn: async () => (await api.get(`${base(schoolId)}/slots`, { params: filters })).data,
    enabled: !!schoolId && enabled,
  });
}

export function useIndividualPricePreview(schoolId: string, sessionId: string | null, partySize: number, bookingType: IndividualBookingType) {
  return useQuery<IndividualPricePreviewResponse>({
    queryKey: ['individualClasses', schoolId, 'price', sessionId, partySize, bookingType],
    queryFn: async () =>
      (await api.get(`${base(schoolId)}/slots/${sessionId}/price`, { params: { partySize, bookingType } })).data,
    enabled: !!schoolId && !!sessionId && partySize > 0,
  });
}

export function useCreateIndividualBooking(schoolId: string) {
  const invalidate = useInvalidateIndividual(schoolId);
  return useMutation({
    mutationFn: async (data: CreateIndividualBookingRequest) =>
      (await api.post(`${base(schoolId)}/bookings`, data)).data as MyIndividualBookingResponse,
    onSuccess: () => {
      invalidate();
      toast.success('Booking requested', 'individualClasses.toasts.bookingRequested');
    },
    onError: (e) => toastApiError(e, 'Failed to book'),
  });
}

export function useMyIndividualBookings(schoolId: string) {
  return useQuery<MyIndividualBookingResponse[]>({
    queryKey: ['individualClasses', schoolId, 'myBookings'],
    queryFn: async () => (await api.get(`${base(schoolId)}/bookings/me`)).data,
    enabled: !!schoolId,
  });
}

export function useCancelIndividualBooking(schoolId: string) {
  const invalidate = useInvalidateIndividual(schoolId);
  return useMutation({
    mutationFn: async ({ bookingId, acceptLateFee }: { bookingId: string; acceptLateFee: boolean }) => {
      await api.post(`${base(schoolId)}/bookings/${bookingId}/cancel`, { acceptLateFee });
    },
    onSuccess: () => {
      invalidate();
      toast.success('Booking cancelled', 'individualClasses.toasts.bookingCancelled');
    },
    onError: (e) => toastApiError(e, 'Failed to cancel'),
  });
}

export function useResignIndividualFixedSlot(schoolId: string) {
  const invalidate = useInvalidateIndividual(schoolId);
  return useMutation({
    mutationFn: async (scheduleEnrollmentId: string) => {
      await api.post(`${base(schoolId)}/fixed-slots/${scheduleEnrollmentId}/resign`);
    },
    onSuccess: () => {
      invalidate();
      toast.success('Resignation saved', 'individualClasses.toasts.resigned');
    },
    onError: (e) => toastApiError(e, 'Failed to resign'),
  });
}

// ── Coach / Admin ─────────────────────────────────────────────────────────

export function useCoachIndividualSlots(schoolId: string, filters: IndividualSlotFilters, enabled = true) {
  return useQuery<IndividualSlotResponse[]>({
    queryKey: ['individualClasses', schoolId, 'coachSlots', filters],
    queryFn: async () => (await api.get(`${base(schoolId)}/coach/slots`, { params: filters })).data,
    enabled: !!schoolId && enabled,
  });
}

export function useIndividualSchedules(schoolId: string, coachMemberId?: string, enabled = true) {
  return useQuery<IndividualScheduleResponse[]>({
    queryKey: ['individualClasses', schoolId, 'schedules', coachMemberId],
    queryFn: async () => (await api.get(`${base(schoolId)}/coach/schedules`, { params: { coachMemberId } })).data,
    enabled: !!schoolId && enabled,
  });
}

export function useCreateIndividualSlot(schoolId: string) {
  const invalidate = useInvalidateIndividual(schoolId);
  return useMutation({
    mutationFn: async (data: CreateIndividualSlotRequest) =>
      (await api.post(`${base(schoolId)}/coach/slots`, data)).data as IndividualSlotResponse[],
    onSuccess: () => {
      invalidate();
      toast.success('Slot created', 'individualClasses.toasts.slotCreated');
    },
    onError: (e) => toastApiError(e, 'Failed to create the slot'),
  });
}

export function useUpdateIndividualSlot(schoolId: string) {
  const invalidate = useInvalidateIndividual(schoolId);
  return useMutation({
    mutationFn: async ({ sessionId, data }: { sessionId: string; data: UpdateIndividualSlotRequest }) =>
      (await api.put(`${base(schoolId)}/coach/slots/${sessionId}`, data)).data as IndividualSlotResponse,
    onSuccess: () => {
      invalidate();
      toast.success('Slot updated', 'individualClasses.toasts.slotUpdated');
    },
    onError: (e) => toastApiError(e, 'Failed to update the slot'),
  });
}

export function useCancelIndividualSlot(schoolId: string) {
  const invalidate = useInvalidateIndividual(schoolId);
  return useMutation({
    mutationFn: async ({ sessionId, reason }: { sessionId: string; reason: string | null }) => {
      await api.post(`${base(schoolId)}/coach/slots/${sessionId}/cancel`, { reason });
    },
    onSuccess: () => {
      invalidate();
      toast.success('Slot cancelled', 'individualClasses.toasts.slotCancelled');
    },
    onError: (e) => toastApiError(e, 'Failed to cancel the slot'),
  });
}

export function useEndIndividualSchedule(schoolId: string) {
  const invalidate = useInvalidateIndividual(schoolId);
  return useMutation({
    mutationFn: async (scheduleId: string) => {
      await api.post(`${base(schoolId)}/coach/schedules/${scheduleId}/end`);
    },
    onSuccess: () => {
      invalidate();
      toast.success('Recurring slot ended', 'individualClasses.toasts.scheduleEnded');
    },
    onError: (e) => toastApiError(e, 'Failed to end the recurring slot'),
  });
}

export function useIndividualBookingRequests(schoolId: string, coachMemberId?: string, enabled = true) {
  return useQuery<CoachIndividualBookingResponse[]>({
    queryKey: ['individualClasses', schoolId, 'requests', coachMemberId],
    queryFn: async () => (await api.get(`${base(schoolId)}/coach/requests`, { params: { coachMemberId } })).data,
    enabled: !!schoolId && enabled,
    refetchInterval: 60_000,
  });
}

export function useApproveIndividualBooking(schoolId: string) {
  const invalidate = useInvalidateIndividual(schoolId);
  return useMutation({
    mutationFn: async (bookingId: string) => {
      await api.post(`${base(schoolId)}/coach/bookings/${bookingId}/approve`);
    },
    onSuccess: () => {
      invalidate();
      toast.success('Booking approved', 'individualClasses.toasts.bookingApproved');
    },
    onError: (e) => toastApiError(e, 'Failed to approve'),
  });
}

export function useRejectIndividualBooking(schoolId: string) {
  const invalidate = useInvalidateIndividual(schoolId);
  return useMutation({
    mutationFn: async ({ bookingId, reason }: { bookingId: string; reason: string | null }) => {
      await api.post(`${base(schoolId)}/coach/bookings/${bookingId}/reject`, { reason });
    },
    onSuccess: () => {
      invalidate();
      toast.success('Booking rejected', 'individualClasses.toasts.bookingRejected');
    },
    onError: (e) => toastApiError(e, 'Failed to reject'),
  });
}

export function useCoachCancelIndividualBooking(schoolId: string) {
  const invalidate = useInvalidateIndividual(schoolId);
  return useMutation({
    mutationFn: async ({ bookingId, charge, reason }: { bookingId: string; charge: boolean; reason: string | null }) => {
      await api.post(`${base(schoolId)}/coach/bookings/${bookingId}/cancel`, { charge, reason });
    },
    onSuccess: () => {
      invalidate();
      toast.success('Booking cancelled', 'individualClasses.toasts.bookingCancelled');
    },
    onError: (e) => toastApiError(e, 'Failed to cancel'),
  });
}

export function useIndividualOnSiteBills(schoolId: string, coachMemberId?: string, enabled = true) {
  return useQuery<OnSiteBillResponse[]>({
    queryKey: ['individualClasses', schoolId, 'onSiteBills', coachMemberId],
    queryFn: async () => (await api.get(`${base(schoolId)}/coach/bills`, { params: { coachMemberId } })).data,
    enabled: !!schoolId && enabled,
  });
}

export function useMarkIndividualBillPaid(schoolId: string) {
  const invalidate = useInvalidateIndividual(schoolId);
  return useMutation({
    mutationFn: async ({ billId, method }: { billId: string; method: OnSitePaymentMethod }) => {
      await api.post(`${base(schoolId)}/coach/bills/${billId}/mark-paid`, { method });
    },
    onSuccess: () => {
      invalidate();
      toast.success('Payment recorded', 'individualClasses.toasts.paymentRecorded');
    },
    onError: (e) => toastApiError(e, 'Failed to record the payment'),
  });
}
